import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import playerPlaceholder from '@sorare/core/src/assets/players/placeholder.webp';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { DisplayM, LabelL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Card } from '@sorare/core/src/components/card/Card';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import {
  getPlayerInjuryColor,
  getPlayerInjuryMessage,
  isHitter,
  isPitcher,
} from '@sorare/core/src/lib/baseball';
import { playerUnavailability } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import {
  anyPositionShortNames,
  positionNames,
} from '@sorare/core/src/lib/players';
import type { Color } from '@sorare/core/src/style/types';

import { useAnySportEvents } from '#src/shared/events/lib.ts';
import { HandInfoChip } from '#src/shared/player/HandInfoChip/index.tsx';

import type {
  PlayerProperties_anyCard,
  PlayerProperties_anyPlayer,
} from './__generated__/index.graphql.ts';

const IMAGE_WIDTH = 140;
const IMAGE_HEIGHT = 126;

const Root = styled(SBHorizontal)`
  align-items: stretch;
`;

const CardWrapper = styled.div`
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: var(--intermediate-unit);
  justify-content: space-between;
`;

const PlayerImageWrapper = styled.div`
  position: relative;
  &::before {
    position: absolute;
    content: '';
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0) 0%,
      var(--c-black) 100%
    );
  }
`;

const PlayerImage = styled(ResponsiveImg)`
  width: 140px;
  height: 126px;
  object-fit: contain;
`;

const WrapHorizontal = styled(Horizontal)`
  align-items: end;
  flex-wrap: wrap;
`;

const StyledChip = styled(Chip)`
  --rgb-color: 240, 206, 29;
  background: linear-gradient(
    -30deg,
    rgba(var(--rgb-color), 0.16) 50%,
    var(--c-nd-100) 50%
  );
  border: 1px solid rgba(var(--rgb-color), 0.3);

  &.suspension {
    --rgb-color: var(--c-rgb-red-400);
  }
`;

const PlayerName = styled(DisplayM)`
  word-break: break-word;
`;
const ClubAvatar = styled.img`
  width: var(--intermediate-unit);
  height: var(--intermediate-unit);
  object-fit: contain;
  object-position: center;
`;

const SBVertical = styled(Vertical)`
  justify-content: space-between;
`;
type Props = {
  player: PlayerProperties_anyPlayer;
  card?: PlayerProperties_anyCard;
  useCardPositions?: boolean;
  usePlayerPicture?: boolean;
  usePlayerAvailability?: boolean;
  disableLinks?: boolean;
};

export const PlayerProperties = ({
  player,
  card,
  usePlayerPicture,
  useCardPositions,
  usePlayerAvailability = true,
  disableLinks,
}: Props) => {
  const {
    displayName,
    anyPositions,
    squaredPictureUrl: playerPictureUrl,
  } = player;
  const { generateSportContextPath } = useSportContext();
  const { isIosApp } = useIsMobileApp();
  const track = useAnySportEvents();

  let availability: MessageDescriptor = playerUnavailability.active;
  let color: Color = 'var(--c-score-high)';

  const isInjured = player.activeInjuries.length > 0;
  const isSuspended = player.activeSuspensions.length > 0;

  if (isType(player, 'BaseballPlayer') && player.activeInjuries.length) {
    availability = getPlayerInjuryMessage(player.activeInjuries[0].status, {
      long: true,
    });
    color = `var(--c-${getPlayerInjuryColor(player.activeInjuries[0].status)}-600)`;
  } else if (isInjured) {
    availability = playerUnavailability.injuryTitle;
    color = 'var(--c-yellow-400)';
  } else if (isSuspended) {
    availability = playerUnavailability.suspendedTitle;
    color = 'var(--c-red-400)';
  }

  const playerPositions =
    useCardPositions && card ? card.anyPositions : anyPositions;
  return (
    <Root>
      <Vertical>
        {!!usePlayerAvailability && (
          <LabelL color={color}>
            <FormattedMessage {...availability} />
          </LabelL>
        )}
        <Wrapper>
          <SBVertical>
            <PlayerName>
              {isIosApp || disableLinks ? (
                displayName
              ) : (
                <Link
                  to={generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
                    params: { slug: player.slug },
                    sport: player.sport,
                  })}
                  onClick={() =>
                    track('Navigate To Player Page', {
                      playerSlug: player.slug,
                      interactionContext: 'player_details_drawer',
                    })
                  }
                >
                  {displayName}
                </Link>
              )}
            </PlayerName>
            <WrapHorizontal gap={0.5}>
              {playerPositions.map(position => (
                <span key={position}>
                  <Tooltip
                    title={<FormattedMessage {...positionNames[position]} />}
                  >
                    <Chip>
                      <FormattedMessage {...anyPositionShortNames[position]} />
                    </Chip>
                  </Tooltip>
                </span>
              ))}
              {isType(player, 'BaseballPlayer') && (
                <>
                  {isHitter(playerPositions) && (
                    <HandInfoChip position="batting" player={player} />
                  )}
                  {isPitcher(playerPositions) && (
                    <HandInfoChip position="pitching" player={player} />
                  )}
                </>
              )}
              {!!player.activeClub && (
                <span>
                  <Tooltip title={player.activeClub.name}>
                    <Chip>
                      <ClubAvatar
                        alt=""
                        src={player.activeClub.pictureUrl || ''}
                      />
                      {player.activeClub.code}
                    </Chip>
                  </Tooltip>
                </span>
              )}
              {!!(isInjured || isSuspended) && (
                <StyledChip
                  color="yellow"
                  className={classNames({
                    suspension: player.activeSuspensions.length > 0,
                  })}
                >
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    color={
                      player.activeInjuries.length > 0
                        ? 'var(--c-yellow-400)'
                        : 'var(--c-red-400)'
                    }
                  />
                </StyledChip>
              )}
            </WrapHorizontal>
          </SBVertical>
        </Wrapper>
      </Vertical>
      {!usePlayerPicture && card ? (
        <CardWrapper>
          <Card card={card} />
        </CardWrapper>
      ) : (
        <PlayerImageWrapper>
          <PlayerImage
            src={playerPictureUrl || playerPlaceholder}
            cropWidth={160}
            width={IMAGE_WIDTH}
            height={IMAGE_HEIGHT}
            alt=""
          />
        </PlayerImageWrapper>
      )}
    </Root>
  );
};

PlayerProperties.fragments = {
  anyPlayer: gql`
    fragment PlayerProperties_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      sport
      activeInjuries {
        id
        status
      }
      activeSuspensions {
        id
      }
      activeClub {
        slug
        code
        name
        pictureUrl
      }
      squaredPictureUrl
      anyPositions
      ... on BaseballPlayer {
        slug
        ...HandInfoChip_BaseballPlayer
      }
    }
    ${HandInfoChip.fragments.BaseballPlayer}
  ` as TypedDocumentNode<PlayerProperties_anyPlayer>,
  anyCard: gql`
    fragment PlayerProperties_anyCard on AnyCardInterface {
      slug
      anyPositions
      ...Card_anyCard
    }
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<PlayerProperties_anyCard>,
};
